import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

/** Query string with %20 for spaces (not +) and indexed arrays: customer_id[0] */
const buildApiQueryString = (params = {}) => {
  const parts = [];
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const baseKey = String(key).replace(/\[\]$/, "");
      let index = 0;
      value.forEach((item) => {
        if (item !== "" && item != null && item !== undefined) {
          parts.push(
            `${encodeURIComponent(`${baseKey}[${index}]`)}=${encodeURIComponent(String(item))}`
          );
          index += 1;
        }
      });
    } else if (value !== "" && value != null && value !== undefined) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  });
  return parts.join("&");
};

// Fetch trip requests
export const fetchTripRequests = createAsyncThunk(
  "tripRequests/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const qs = buildApiQueryString(params);
      const url = `${BASE_URL}/travel-requests${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load trip requests";
      return rejectWithValue({ message });
    }
  }
);

// Fetch single trip request
export const fetchTripRequest = createAsyncThunk(
  "tripRequests/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/travel-requests/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load trip request";
      return rejectWithValue({ message });
    }
  }
);

// Update trip request status
export const updateTripRequestStatus = createAsyncThunk(
  "tripRequests/updateStatus",
  async ({ travel_request_id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("travel_request_id", travel_request_id);
      formData.append("status", status);
      
      const response = await axios.post(`${BASE_URL}/travel-requests/update-status`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update trip request status";
      return rejectWithValue({ message });
    }
  }
);

// Update trip request
export const updateTripRequest = createAsyncThunk(
  "tripRequests/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(`${BASE_URL}/travel-requests/${id}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update trip request";
      return rejectWithValue({ message });
    }
  }
);

// Fetch business sectors
export const fetchBusinessSectors = createAsyncThunk(
  "tripRequests/fetchBusinessSectors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const url = `${BASE_URL}/business-sectors${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sectors";
      return rejectWithValue({ message });
    }
  }
);

// Fetch clients
export const fetchClients = createAsyncThunk(
  "tripRequests/fetchClients",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const url = `${BASE_URL}/clients${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load clients";
      return rejectWithValue({ message });
    }
  }
);

// Fetch winch partners
export const fetchWinchPartners = createAsyncThunk(
  "tripRequests/fetchWinchPartners",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const url = `${BASE_URL}/partners${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      return rejectWithValue({ message });
    }
  }
);

const TripRequestsSlice = createSlice({
  name: "tripRequests",
  initialState: {
    tripRequests: [],
    pagination: null,
    totalRequestNew: 0,
    totalRequestAccepted: 0,
    totalRequestTripStarted: 0,
    totalRequestCompleted: 0,
    totalRequestCanceled: 0,
    record: null,
    businessSectors: [],
    businessSectorsPagination: null,
    clients: [],
    clientsPagination: null,
    winchPartners: [],
    winchPartnersPagination: null,
    businessSectorsLoading: false,
    clientsLoading: false,
    winchPartnersLoading: false,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTripRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTripRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tripRequests = action.payload?.data || [];
        state.pagination = action.payload?.pagination || null;
        state.totalRequestNew = action.payload?.total_request_new || 0;
        state.totalRequestAccepted = action.payload?.total_request_accepted || 0;
        state.totalRequestTripStarted = action.payload?.total_request_trip_started || 0;
        state.totalRequestCompleted = action.payload?.total_request_completed || 0;
        state.totalRequestCanceled = action.payload?.total_request_canceled || 0;
      })
      .addCase(fetchTripRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load trip requests";
      })
      // Fetch one
      .addCase(fetchTripRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTripRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchTripRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load trip request";
      })
      // Update status
      .addCase(updateTripRequestStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTripRequestStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Status updated successfully";
        // Update the record if it exists
        if (state.record && state.record.id === action.payload?.data?.id) {
          state.record = { ...state.record, ...action.payload.data };
        }
      })
      .addCase(updateTripRequestStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update status";
      })
      // Update trip request
      .addCase(updateTripRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTripRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Trip request updated successfully";
        // Update the record if it exists
        if (state.record && state.record.id === action.payload?.data?.id) {
          state.record = { ...state.record, ...action.payload.data };
        }
      })
      .addCase(updateTripRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to update trip request";
      })
      // Fetch business sectors
      .addCase(fetchBusinessSectors.pending, (state) => {
        state.businessSectorsLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectors.fulfilled, (state, action) => {
        state.businessSectorsLoading = false;
        const newData = action.payload?.data || [];
        if (action.payload?.pagination?.current_page === 1) {
          state.businessSectors = newData;
        } else {
          state.businessSectors = [...state.businessSectors, ...newData];
        }
        state.businessSectorsPagination = action.payload?.pagination || null;
      })
      .addCase(fetchBusinessSectors.rejected, (state, action) => {
        state.businessSectorsLoading = false;
        state.error = action.payload?.message || "Failed to load business sectors";
      })
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.clientsLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.clientsLoading = false;
        const newData = action.payload?.data || [];
        if (action.payload?.pagination?.current_page === 1) {
          state.clients = newData;
        } else {
          state.clients = [...state.clients, ...newData];
        }
        state.clientsPagination = action.payload?.pagination || null;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.clientsLoading = false;
        state.error = action.payload?.message || "Failed to load clients";
      })
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.winchPartnersLoading = true;
        state.error = null;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.winchPartnersLoading = false;
        const newData = action.payload?.data || [];
        if (action.payload?.pagination?.current_page === 1) {
          state.winchPartners = newData;
        } else {
          state.winchPartners = [...state.winchPartners, ...newData];
        }
        state.winchPartnersPagination = action.payload?.pagination || null;
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.winchPartnersLoading = false;
        state.error = action.payload?.message || "Failed to load winch partners";
      });
  },
});

export const { clearState } = TripRequestsSlice.actions;
export default TripRequestsSlice.reducer;
